import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import ConsistencyLogo from '@/components/ConsistencyLogo';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChoosePlan() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [trialEligibility, setTrialEligibility] = useState(null);
  const [stripeMockMode, setStripeMockMode] = useState(false);

  useEffect(() => {
    loadPlans();
    checkTrialEligibility();
    checkStripeMode();
    
    // Check if user came from pricing page with pre-selected plan
    const preSelectedPlan = localStorage.getItem('selected_plan');
    if (preSelectedPlan) {
      setSelectedPlan(preSelectedPlan);
      // Optionally auto-trigger checkout
      // handleSelectPlan(preSelectedPlan);
    }
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/public/plans`);
      const data = await res.json();
      // Only show Starter, Creator, Pro for onboarding
      const onboardingPlans = data.plans.filter(p => 
        ['starter', 'creator', 'pro'].includes(p.key)
      );
      setPlans(onboardingPlans);
    } catch (error) {
      console.error('Error loading plans:', error);
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const checkTrialEligibility = async () => {
    try {
      const res = await fetch(`${API}/stripe/trial-eligibility`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setTrialEligibility(data);
      }
    } catch (error) {
      console.error('Failed to check trial eligibility:', error);
    }
  };

  const checkStripeMode = async () => {
    try {
      const res = await fetch(`${API}/stripe/mode`);
      if (res.ok) {
        const data = await res.json();
        setStripeMockMode(data.mock_enabled || false);
      }
    } catch (error) {
      console.error('Failed to check Stripe mode:', error);
    }
  };

  const handleSelectPlan = async (planKey) => {
    setSelectedPlan(planKey);
    
    try {
      // Check if this is Pro and user is eligible for trial (Phase 3A)
      const applyTrial = planKey === 'pro' && trialEligibility?.eligible;
      
      if (applyTrial) {
        toast.success('Starting your 7-day Pro trial...');
      } else {
        toast.success('Redirecting to Stripe checkout...');
      }
      
      // Store selected plan
      sessionStorage.setItem('selected_plan', planKey);
      
      // Call backend to create Stripe checkout session
      const res = await fetch(`${API}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          plan_key: planKey,
          billing_cycle: 'monthly',
          apply_trial: applyTrial  // Phase 3A: Apply trial if eligible
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to create checkout session');
      }
      
      const data = await res.json();
      
      // Check if URL contains mock parameter (mock mode)
      if (data.checkout_url.includes('mock=true')) {
        // Mock mode: Simulate successful checkout
        toast.success(`Mock mode: ${planKey} plan activated!`);
        setTimeout(() => {
          navigate('/onboarding/creator-setup');
        }, 1000);
      } else {
        // Real mode: Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Failed to start checkout');
      setSelectedPlan(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121417] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121417] p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-center mb-6">
          <ConsistencyLogo variant="wordmark" size="md" />
        </div>

        {/* Mock Mode Banner */}
        {stripeMockMode && (
          <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-sm text-yellow-400 text-center">
              <span className="font-semibold">Test Mode:</span> Stripe is running in mock mode. No real charges will be made.
            </p>
          </div>
        )}

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Choose Your Plan</h1>
          <p className="text-[#8A93A6]">Start with the plan that fits your needs</p>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {plans.map((plan) => (
            <Card
              key={plan.key}
              className={`glass relative ${
                plan.popular ? 'ring-2 ring-pink-500' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-pink-500 text-white">Most Popular</Badge>
                </div>
              )}
              
              {/* Phase 3A: 7-Day Trial Badge for Pro */}
              {plan.key === 'pro' && trialEligibility?.eligible && (
                <div className="absolute -top-3 right-4">
                  <Badge className="bg-blue-500 text-white">7-Day Free Trial</Badge>
                </div>
              )}
              
              <CardHeader>
                <CardTitle className="text-white">{plan.name}</CardTitle>
                <CardDescription className="text-[#8A93A6]">
                  Perfect for getting started
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Price */}
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-white">
                      ${plan.monthly_price}
                    </span>
                    <span className="text-[#8A93A6]">/mo</span>
                  </div>
                  {plan.launch_pricing_active && (
                    <p className="text-sm text-green-400 mt-1">
                      Launch pricing - Limited time!
                    </p>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-2">
                  {plan.features.slice(0, 5).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-[#8A93A6]">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Button
                  onClick={() => handleSelectPlan(plan.key)}
                  disabled={selectedPlan !== null}
                  className={`w-full ${
                    plan.popular
                      ? 'bg-pink-500 hover:bg-pink-600'
                      : 'bg-[#3A7BFF] hover:bg-[#2D6FE8]'
                  }`}
                >
                  {selectedPlan === plan.key ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Choose {plan.name}
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <p className="text-center text-sm text-[#8A93A6]">
          You can upgrade or downgrade anytime from your account settings
        </p>
      </div>
    </div>
  );
}
